import { useEffect, useRef, useState } from "react";
import { statsCounterData } from "@/mocks/homeData";

function AnimatedCounter({
  target,
  suffix,
  duration = 2000,
}: {
  target: number;
  suffix: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = performance.now();
          const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(easeOut * target));
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(target);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, hasAnimated]);

  return (
    <div ref={ref} className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-accent-500">
      {count}
      {suffix}
    </div>
  );
}

export default function StatsCounterSection() {
  return (
    <section className="w-full py-16 md:py-24 bg-foreground-950">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <span className="text-sm font-semibold text-accent-500 uppercase tracking-wider">
            By The Numbers
          </span>
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-background-50 mt-2 mb-4">
            {statsCounterData.headline}
          </h2>
          <p className="text-background-400 leading-relaxed">
            {statsCounterData.subheadline}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {statsCounterData.stats.map((stat) => (
            <div
              key={stat.label}
              className="text-center p-6 md:p-8 rounded-xl bg-foreground-900/50 border border-foreground-800/60"
            >
              <AnimatedCounter target={stat.value} suffix={stat.suffix} />
              <div className="mt-3 text-sm md:text-base text-background-400">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}